import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { T, useIsDesktop, Bone } from '../reportTokens';
import DrillFrame, { MiniKPI } from './DrillFrame';
import SectionError from '../SectionError';
import SectionEmpty from '../SectionEmpty';
import { fmtNum, dateTickInterval, makeDateTickFormatter, DATE_AXIS_PADDING, dateAxisRightMargin, dateAxisTickProps, dateAxisExtraHeight } from './offersData';
import type { OfferRedemptionTrendResponse } from '../../../interface/offersReportInterface';

// Descending opacity for the order-type bars so the ranking reads at a glance.
const BAR_ALPHAS = ['CC', '99', '66', '40', '2E', '20'];

const SECTION_TITLE_STYLE: React.CSSProperties = { fontSize: 11, fontWeight: 600, color: T.t2, textTransform: 'uppercase', letterSpacing: 0.3, marginBottom: 10 };

export default function OfferRedemptionTrendDrill({ data, loading, failure, onRetry }: { data: OfferRedemptionTrendResponse | null; loading?: boolean; failure?: boolean; onRetry?: () => void }) {
    const isDesktop = useIsDesktop();

    const daily = data?.dailyTrend ?? [];
    const total = data?.totalRedemptions ?? 0;
    const avg = data?.avgDailyRedemptions ?? 0;
    // Peak Day is FE-derived: the day with the highest redemption count.
    const peak = daily.length ? daily.reduce((m, d) => (d.count > m.count ? d : m), daily[0]) : null;

    // Order Type Breakdown — rendered dynamically from the API, ranked by count.
    const rows = [...(data?.orderTypeBreakdown ?? [])].sort((a, b) => b.count - a.count);
    const maxCount = rows.length ? Math.max(...rows.map((r) => r.count)) : 0;

    // Each section owns its state — the three panels are fed by one API, but a
    // failure/empty is rendered in-place per section (shaped like that section)
    // rather than collapsing the whole drill into a single message.
    const isLoading = !!loading && !data;

    // Chart + placeholder height. Below desktop the x-axis labels are rotated
    // (`dateAxisTickProps`) and the extra height pays for the taller tick band.
    const chartHeight = (isDesktop ? 220 : 160) + dateAxisExtraHeight(isDesktop);

    return (
        <DrillFrame isDesktop={isDesktop}>
            <div style={{ background: T.card, border: `1px solid ${T.cardBorder}`, borderRadius: 14, padding: 18, boxShadow: T.shadow }}>
                {/* ── Stamp KPIs — on failure/empty the cards stay put and show "—" ── */}
                <div style={{ marginBottom: 14 }}>
                    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                        <MiniKPI label="Total Redemptions" value={isLoading ? <Bone w={80} h={22} r={4} /> : data ? fmtNum(total) : '—'} />
                        <MiniKPI label="Peak Day" value={isLoading ? <Bone w={80} h={22} r={4} /> : (data && peak) ? <>
                            <div style={{ fontSize: 11, color: T.t2, marginBottom: 2 }}>{new Date(peak.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</div>
                            {fmtNum(peak.count)}
                        </> : '—'} />
                        <MiniKPI label="Avg Daily" value={isLoading ? <Bone w={80} h={22} r={4} /> : data ? fmtNum(avg) : '—'} />
                    </div>
                </div>

                {/* ── Trend chart ────────────────────────────────────────────── */}
                <div style={{ marginBottom: 16 }}>
                    {isLoading ? (
                        <Bone h={chartHeight} r={8} />
                    ) : failure ? (
                        <SectionError kpiName="redemption trend" onRetry={onRetry} size="medium" minHeight={chartHeight} />
                    ) : daily.length === 0 ? (
                        <SectionEmpty kpiName="redemption trend" size="medium" minHeight={chartHeight} />
                    ) : (
                        <ResponsiveContainer width="100%" height={chartHeight}>
                            <LineChart data={daily} margin={{ top: 10, right: dateAxisRightMargin(isDesktop), left: -10, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke={T.divider} />
                                <XAxis dataKey="date" tick={{ fontSize: 9, fill: T.t3 }} axisLine={false} tickLine={false} padding={DATE_AXIS_PADDING} tickFormatter={makeDateTickFormatter(daily.map((d) => d.date))} interval={dateTickInterval(daily.length)} {...dateAxisTickProps(isDesktop)} />
                                <YAxis tick={{ fontSize: 9, fill: T.t3 }} axisLine={false} tickLine={false} />
                                <Tooltip contentStyle={{ background: T.surface, border: `1px solid ${T.cardBorder}`, borderRadius: 8, fontSize: 11 }} />
                                <Line type="monotone" dataKey="count" stroke={T.accent} strokeWidth={2.5} dot={false} />
                            </LineChart>
                        </ResponsiveContainer>
                    )}
                </div>

                {/* ── Order Type Breakdown (progress bars) ───────────────────── */}
                <div>
                    <div style={SECTION_TITLE_STYLE}>Order Type Breakdown</div>
                    {isLoading ? (
                        [0, 1, 2, 3].map((i) => (
                            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                                <Bone w={80} h={11} r={4} />
                                <Bone w={null} h={6} r={3} style={{ flex: 1 }} />
                                <Bone w={40} h={11} r={4} />
                            </div>
                        ))
                    ) : failure ? (
                        <SectionError kpiName="order type breakdown" onRetry={onRetry} size="small" minHeight={120} />
                    ) : rows.length === 0 ? (
                        <SectionEmpty kpiName="order type breakdown" size="small" minHeight={120} />
                    ) : (
                        rows.map((r, i) => (
                            <div key={r.orderType} style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                                <div style={{ width: 80, fontSize: 11, color: T.t2 }}>{r.orderType}</div>
                                <div style={{ flex: 1, background: T.hover, borderRadius: 3, height: 6, overflow: 'hidden' }}>
                                    <div style={{ width: `${maxCount > 0 ? (r.count / maxCount) * 100 : 0}%`, height: '100%', background: `${T.accent}${BAR_ALPHAS[Math.min(i, BAR_ALPHAS.length - 1)]}` }} />
                                </div>
                                <div style={{ width: 50, fontSize: 11, fontWeight: 600, color: T.t1, textAlign: 'right' }}>{fmtNum(r.count)}</div>
                            </div>
                        ))
                    )}
                </div>
            </div>
        </DrillFrame>
    );
}
